import { ArrowLeftIcon, LinkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';

import { formatDate } from '../../../../common-utils/formatters';
import type { CompanyBailExtended, Copro } from '../../../@types/Copro';
import { useAppSelector } from '../../../store/hook';
import { BauxDict } from '../../../utils/dictionnayMap';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup/Popup.module';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';

const headers = [
  { title: 'SIREN' },
  { title: 'Nom SIREN' },
  { title: 'Adresse' },
  { title: 'Date potentielle de période triennale' },
  { title: 'Voir le détail', class: 'min-w-[120px]' },
];

const sortableColumns = [
  'SIREN', 'Nom SIREN', 'Adresse', 'Date potentielle de période triennale',
];

function BauxCopro() {
  const { coproId } = useParams<{ coproId: string }>();
  const navigate = useNavigate();
  const jwt = useAppSelector((state) => state.auth.token);
  
  const [baux, setBaux] = useState<CompanyBailExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [copro, setCopro] = useState<Copro | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRowDetails, setSelectedRowDetails] = useState<number | null>(null);

  useEffect(() => {
    const fetchCoproDetails = async() => {
      try {
        const response = await fetch(`/api/copros/${coproId}`, JWTHeader(jwt));
        if (!response.ok) {
          throw new Error(`HTTP error status: ${response.status}`);
        }
        const data = await response.json();
        setCopro(data);
      } catch (error) {
        console.error('Error fetching copro details :', error);
        showToast('error', 'Une erreur est survenue lors du chargement des détails de la copropriété.');
      }
    };

    const fetchBauxForCopro = async() => {
      try {
        setLoading(true);
        const response = await fetch(`/api/copros/${coproId}/baux`, JWTHeader(jwt));
        if (!response.ok) {
          throw new Error(`HTTP error status: ${response.status}`);
        }
        const data = await response.json();
        
        // Sort baux by lease end date (ascending)
        const sortedData = [...data].sort((a, b) => {
          const dateA = new Date(a.leaseEndDate);
          const dateB = new Date(b.leaseEndDate);
          return dateA.getTime() - dateB.getTime();
        });
        
        setBaux(sortedData);
      } catch (error) {
        console.error('Error fetching baux for copro :', error);
        showToast('error', 'Une erreur est survenue lors du chargement des baux commerciaux.');
      } finally {
        setLoading(false);
      }
    };

    if (coproId && jwt) {
      fetchCoproDetails();
      fetchBauxForCopro();
    }
  }, [coproId, jwt]);

  const handleDetails = (index: number) => {
    setIsPopupOpen(true);
    setSelectedRowDetails(index);
  };

  const handleGoBack = () => {
    // Try to go back to the previous route in history
    // If there's no previous route (e.g., user opened this page directly), 
    // navigate to the baux commerciaux list
    try {
      // Check if we can go back in history
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1); // Go back to the previous route
      } else {
        // No previous route in history, go to the default route
        navigate(ROUTES.MONITORING_BAUX_COMMERCIAUX);
      }
    } catch (error) {
      // Fallback in case of any issues
      console.error('Navigation error :', error);
      navigate(ROUTES.MONITORING_BAUX_COMMERCIAUX);
    }
  };

  const renderNestedObject = (obj: Record<string, unknown>, parentKey = '') => (
    <MUITable
      data={Object.keys(obj).map((key) => {
        const value = obj[key];
        const displayKey = parentKey ? `${parentKey}.${key}` : key;
        const displayLabel = BauxDict[key] ? BauxDict[key] : key;
        if (typeof value === 'object' && value !== null) {
          const objValue = value as Record<string, unknown>;
          return {
            Libellé: <strong>{displayLabel}</strong>,
            Valeur: (
              <div key={displayKey}>
                {key === 'gestionnaireCopro'
                  ? (objValue.email ? `${String(objValue.firstname ?? '')} ${objValue.lastname ? String(objValue.lastname).toUpperCase() : ''} - ${String(objValue.email ?? '')}` : 'N/A')
                  : renderNestedObject(objValue, displayKey)}
              </div>
            ),
          };
        }
        return {
          Libellé: <strong>{displayLabel}</strong>,
          Valeur: (
            <div key={displayKey}>{value as React.ReactNode}</div>
          ),
        };
      })}
      headers={[
        { title: 'Libellé', class: 'w-1/2', customContentClass: 'w-1/2' },
        { title: 'Valeur', class: 'w-1/2', customContentClass: 'w-1/2 break-words break-all whitespace-normal' },
      ]}
      isSearchable={false}
      disablePagination
      tableClass="table-fixed"
    />
  );

  const renderPopupTitle = (selectedIndex: number | null) => {
    if (selectedIndex === null || !baux || !baux[selectedIndex]) {return "Détail de l'entreprise";}
    
    const siren = baux[selectedIndex].siren;
    
    return (
      <div className="items-center md:flex md:justify-center">
        <span>Détail de l'entreprise</span>
        {siren && (
          <a 
            href={`https://www.pappers.fr/entreprise/${siren}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 ml-0 flex w-full items-center rounded bg-rgpd-blue-dark px-3 py-2 text-sm text-white shadow-md md:mt-0 md:ml-4 md:w-auto"
          >
            <LinkIcon className="mr-1 h-4 w-4" />
            Ouvrir dans Pappers
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mb-6 flex items-center">
          <button
            onClick={handleGoBack}
            className="mr-4 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold">
            Baux commerciaux - {copro ? copro.name : 'Chargement...'}
          </h1>
        </div>
        
        <div className="mt-4 mb-12 text-lg">
          <p>
            Cette section présente tous les
            {' '}
            <span className="font-bold text-rgpd-blue-light">baux commerciaux</span>
            {' '}
            en cours pour la copropriété {copro ? copro.name : ''}.
            <br />
            {' '}
            Les baux sont triés par date potentielle de période triennale (du plus proche au plus éloigné) pour vous permettre d'anticiper les renouvellements.
          </p>
        </div>
        
        <div>
          <div className="w-full overflow-x-scroll">
            <MUITable
              data={baux ? baux.map((bauxEntry: CompanyBailExtended, index) => {
                const address = [
                  bauxEntry.numeroVoieEtablissement,
                  bauxEntry.typeVoieEtablissement,
                  bauxEntry.libelleVoieEtablissement,
                  bauxEntry.codePostalEtablissement,
                  bauxEntry.libelleCommuneEtablissement,
                ].join(' ');

                return {
                  SIREN: bauxEntry.siren,
                  'Nom SIREN': bauxEntry.denominationUniteLegale
                    || ([
                      bauxEntry.sexeUniteLegale === 'M' ? 'MONSIEUR' : 'MADAME',
                      bauxEntry.prenomUsuelUniteLegale,
                      bauxEntry.nomUniteLegale,
                    ].join(' ')),
                  Adresse: <div className="flex items-center">
                    <div>
                      <RedirectionMapButtons address={address} />
                    </div>
                    <span>{address}</span>
                  </div>,
                  'Date potentielle de période triennale': formatDate(bauxEntry.leaseEndDate),
                  'Voir le détail': <div className="flex justify-center">
                    <button className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto" onClick={() => handleDetails(index)}>
                      Voir le détail
                    </button>
                  </div>,
                };
              }) : []}
              headers={headers}
              tableClass="bg-rgpd-blue-light"
              sortableColumns={sortableColumns}
              isSearchable
              filterColumnName="Date potentielle de période triennale"
            />
            {loading && (
              <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
            )}
            {!loading && baux && baux.length === 0 && (
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucun bail commercial trouvé pour cette copropriété"
                  className="my-10"
                />
              </div>
            )}
          </div>
          {isPopupOpen && (
            <Popup
              isOpen={isPopupOpen}
              title={renderPopupTitle(selectedRowDetails)}
              onClose={() => {
                setIsPopupOpen(false);
                setSelectedRowDetails(null);
              }}
            >
              {selectedRowDetails !== null && baux && (
                <div className="max-h-[75dvh] gap-2 overflow-y-scroll">
                  {renderNestedObject(baux[selectedRowDetails] as unknown as Record<string, unknown>)}
                </div>
              )}
              {loading && (
                <Skeleton count={10} height={25} width={400} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
              )}
            </Popup>
          )}
        </div>
      </div>
    </div>
  );
}

export default BauxCopro; 